import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Checkbox,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import BuildIcon from '@mui/icons-material/Build';

/**
 * Attachments (liitteet) for one invoice.
 *
 * Fetched lazily from its own endpoint rather than nested into the invoice
 * payload: InvoiceDetailSerializer is the response body of every send and update
 * endpoint and is built in loops elsewhere, so it must stay exactly as it was.
 *
 * The component always mounts but renders the visible card ONLY when the invoice
 * actually has attachments — an empty section on every invoice is noise. Adding
 * the first one is therefore driven from the Actions menu, which reaches in
 * through the ref. Mounting unconditionally is what keeps the hidden file input
 * and the work-order dialog available for that.
 */
const InvoiceAttachmentsPanel = forwardRef(function InvoiceAttachmentsPanel({
  invoiceId,
  organizationId,
  invoiceStatus,
  maintenanceId,
  onSummaryChange,
  onNotify,
}, ref) {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);

  // Held in a ref so a new inline callback from the parent cannot retrigger the
  // fetch effect on every render.
  const onSummaryChangeRef = useRef(onSummaryChange);
  onSummaryChangeRef.current = onSummaryChange;

  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [workOrderDialogOpen, setWorkOrderDialogOpen] = useState(false);
  const [noteImages, setNoteImages] = useState([]);
  const [selectedImageIds, setSelectedImageIds] = useState([]);
  const [includeWorkOrderPdf, setIncludeWorkOrderPdf] = useState(false);

  // Attachments follow the same rule as every other invoice edit: draft only.
  // The backend enforces this too; this just keeps the affordances honest.
  const isDraft = invoiceStatus === 'draft';
  const canUpload = isDraft;

  const orgParam = organizationId ? `?organization=${organizationId}` : '';

  const fetchAttachments = useCallback(async () => {
    let items = [];
    try {
      const response = await axios.get(`/api/invoices/${invoiceId}/attachments/${orgParam}`);
      items = response.data || [];
    } catch (err) {
      // A failure here must never break the invoice page.
      items = [];
    } finally {
      setAttachments(items);
      setLoading(false);
      // Single source of truth: the send dialog and the Actions menu read the
      // count and size from here rather than issuing their own request.
      if (onSummaryChangeRef.current) {
        onSummaryChangeRef.current({
          count: items.length,
          totalBytes: items.reduce((sum, a) => sum + (a.file_size_bytes || 0), 0),
        });
      }
    }
  }, [invoiceId, orgParam]);

  useEffect(() => {
    fetchAttachments();
  }, [fetchAttachments]);

  const notify = (message, severity) => {
    if (onNotify) onNotify(message, severity);
  };

  const afterChange = async () => {
    await fetchAttachments();
  };

  const handleFilesSelected = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;

    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));

    setBusy(true);
    try {
      const response = await axios.post(
        `/api/invoices/${invoiceId}/attachments/${orgParam}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const warnings = response.data?.warnings || [];
      if (warnings.length) {
        notify(warnings.join(' '), 'warning');
      } else {
        notify(t('invoiceAttachments.uploaded', 'Liite lisätty'), 'success');
      }
      await afterChange();
    } catch (err) {
      notify(
        err.response?.data?.error || t('invoiceAttachments.uploadFailed', 'Liitteen lisääminen epäonnistui'),
        'error'
      );
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (attachment) => {
    setBusy(true);
    try {
      await axios.delete(`/api/invoices/${invoiceId}/attachments/${attachment.id}/${orgParam}`);
      await afterChange();
    } catch (err) {
      notify(
        err.response?.data?.error || t('invoiceAttachments.deleteFailed', 'Liitteen poistaminen epäonnistui'),
        'error'
      );
    } finally {
      setBusy(false);
    }
  };

  const handleDownload = async (attachment) => {
    try {
      const response = await axios.get(
        `/api/invoices/${invoiceId}/attachments/${attachment.id}/download/${orgParam}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.original_filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      notify(t('invoiceAttachments.downloadFailed', 'Liitteen lataaminen epäonnistui'), 'error');
    }
  };

  const openWorkOrderDialog = async () => {
    setSelectedImageIds([]);
    setIncludeWorkOrderPdf(false);
    setWorkOrderDialogOpen(true);
    try {
      // Note images are nested on the maintenance detail payload; there is no
      // standalone notes endpoint.
      const response = await axios.get(`/api/maintenances/${maintenanceId}/${orgParam}`);
      const images = [];
      (response.data?.notes || []).forEach((note) => {
        (note.images || []).forEach((image) => images.push(image));
      });
      setNoteImages(images);
    } catch (err) {
      setNoteImages([]);
    }
  };

  const handleAddFromWorkOrder = async () => {
    setBusy(true);
    try {
      const response = await axios.post(
        `/api/invoices/${invoiceId}/attachments/from-work-order/${orgParam}`,
        {
          note_image_ids: selectedImageIds,
          include_work_order_pdf: includeWorkOrderPdf,
        }
      );
      const warnings = response.data?.warnings || [];
      if (warnings.length) notify(warnings.join(' '), 'warning');
      setWorkOrderDialogOpen(false);
      await afterChange();
    } catch (err) {
      notify(
        err.response?.data?.error || t('invoiceAttachments.uploadFailed', 'Liitteen lisääminen epäonnistui'),
        'error'
      );
    } finally {
      setBusy(false);
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kt`;
    return `${(bytes / 1024 / 1024).toFixed(1)} Mt`;
  };

  const iconFor = (contentType) =>
    contentType === 'application/pdf' ? <PictureAsPdfIcon fontSize="small" /> : <ImageIcon fontSize="small" />;

  // The Actions menu is the entry point for the FIRST attachment, so it needs to
  // be able to reach the file picker and the work-order dialog from outside.
  useImperativeHandle(ref, () => ({
    openFilePicker: () => fileInputRef.current?.click(),
    openWorkOrderDialog,
  }));

  const hiddenInput = (
    <input
      ref={fileInputRef}
      type="file"
      multiple
      accept="application/pdf,image/jpeg,image/png,image/webp"
      onChange={handleFilesSelected}
      style={{ display: 'none' }}
    />
  );

  const workOrderDialog = (
    <Dialog
      open={workOrderDialogOpen}
      onClose={() => setWorkOrderDialogOpen(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{t('invoiceAttachments.fromWorkOrderTitle', 'Lisää liitteitä työmääräyksestä')}</DialogTitle>
      <DialogContent>
        <FormControlLabel
          control={
            <Checkbox
              checked={includeWorkOrderPdf}
              onChange={(e) => setIncludeWorkOrderPdf(e.target.checked)}
            />
          }
          label={t('invoiceAttachments.includeWorkOrderPdf', 'Työmääräys PDF-tiedostona')}
        />

        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
          {t('invoiceAttachments.noteImages', 'Työmääräyksen kuvat')}
        </Typography>

        {noteImages.length === 0 ? (
          <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
            {t('invoiceAttachments.noNoteImages', 'Työmääräyksellä ei ole kuvia')}
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {noteImages.map((image) => {
              const selected = selectedImageIds.includes(image.id);
              return (
                <Tooltip key={image.id} title={image.original_filename || ''}>
                  <Box
                    onClick={() =>
                      setSelectedImageIds((prev) =>
                        selected ? prev.filter((id) => id !== image.id) : [...prev, image.id]
                      )
                    }
                    sx={{
                      width: 88,
                      height: 88,
                      borderRadius: 1,
                      border: 2,
                      borderColor: selected ? 'primary.main' : 'divider',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      backgroundImage: `url(${image.thumbnail_url || image.url})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }}
                  />
                </Tooltip>
              );
            })}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setWorkOrderDialogOpen(false)}>{t('Cancel')}</Button>
        <Button
          variant="contained"
          onClick={handleAddFromWorkOrder}
          disabled={busy || (!includeWorkOrderPdf && selectedImageIds.length === 0)}
        >
          {busy ? <CircularProgress size={20} /> : t('Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Nothing to show until we know, and nothing to show when the invoice has no
  // attachments — but the input and dialog stay mounted so the Actions menu can
  // still open them.
  if (loading || attachments.length === 0) {
    return (
      <>
        {hiddenInput}
        {workOrderDialog}
      </>
    );
  }

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
        }}
      >
        <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600 }}>
          {t('invoiceAttachments.title', 'Liitteet')}
          <Chip size="small" label={attachments.length} sx={{ ml: 1 }} />
        </Typography>

        {canUpload && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Button
              size="small"
              startIcon={<AttachFileIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={busy}
              sx={{ borderRadius: '8px' }}
            >
              {t('invoiceAttachments.add', 'Lisää liite')}
            </Button>
            {maintenanceId && (
              <Button
                size="small"
                startIcon={<BuildIcon />}
                onClick={openWorkOrderDialog}
                disabled={busy}
                sx={{ borderRadius: '8px' }}
              >
                {t('invoiceAttachments.fromWorkOrder', 'Työmääräyksestä')}
              </Button>
            )}
          </Box>
        )}
      </Box>

      {hiddenInput}

      <List dense disablePadding>
        {attachments.map((attachment) => (
          <ListItem
            key={attachment.id}
            disableGutters
            secondaryAction={
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <IconButton size="small" onClick={() => handleDownload(attachment)}>
                  <DownloadIcon fontSize="small" />
                </IconButton>
                {isDraft && (
                  <IconButton size="small" onClick={() => handleDelete(attachment)} disabled={busy}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            }
          >
            <Box sx={{ mr: 1, color: 'text.secondary', display: 'flex' }}>
              {iconFor(attachment.content_type)}
            </Box>
            <ListItemText
              primary={
                <Typography variant="body2" sx={{ wordBreak: 'break-word', pr: 6 }}>
                  {attachment.original_filename}
                </Typography>
              }
              secondary={formatSize(attachment.file_size_bytes)}
            />
          </ListItem>
        ))}
      </List>

      {workOrderDialog}
    </Paper>
  );
});

export default InvoiceAttachmentsPanel;
